import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { HistoryTask, BpmClient } from "@amaster.ai/bpm-client";

export interface MyHistoryTasksPageProps {
  /** BPM client instance */
  bpmClient: BpmClient;
  /** Current user identifier */
  currentUser: string;
  /** Custom render for task name cell */
  renderTaskName?: (task: HistoryTask) => React.ReactNode;
  /** Custom render for actions cell */
  renderActions?: (task: HistoryTask) => React.ReactNode;
  /** Called when task list is refreshed */
  onRefresh?: () => void;
  /** Additional class name */
  className?: string;
}

/**
 * My History Tasks Page - displays current user's completed tasks
 */
export function MyHistoryTasksPage({
  bpmClient,
  currentUser,
  renderTaskName,
  renderActions,
  onRefresh,
  className = "",
}: MyHistoryTasksPageProps) {
  const { t } = useTranslation("bpm");
  const [tasks, setTasks] = useState<HistoryTask[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await bpmClient.getHistoryTasks({
        taskAssignee: currentUser,
        finished: true,
      });
      if (fetchError) {
        setError(fetchError.message);
      } else {
        setTasks(data || []);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch history");
    } finally {
      setLoading(false);
    }
  }, [bpmClient, currentUser]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleRefresh = () => {
    fetchTasks();
    onRefresh?.();
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (start: string | null | undefined, end: string | null | undefined) => {
    if (!start || !end) return "-";
    const diffMs = new Date(end).getTime() - new Date(start).getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) return `${diffDays}d ${diffHours % 24}h`;
    if (diffHours > 0) return `${diffHours}h ${diffMins % 60}m`;
    return `${diffMins}m`;
  };

  return (
    <div className={`bpm-history-page ${className}`}>
      <div className="bpm-tasks-header">
        <h1 className="bpm-tasks-title">{t("taskList.myHistory", "My Completed Tasks")}</h1>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={loading}
          className="bpm-btn bpm-btn-outline"
        >
          {loading ? t("taskList.loading", "Loading...") : t("taskList.refresh", "Refresh")}
        </button>
      </div>

      {error && <div className="bpm-error">{error}</div>}

      <table className="bpm-table">
        <thead>
          <tr>
            <th>{t("taskList.taskName", "Task Name")}</th>
            <th>{t("taskList.processInstance", "Process Instance")}</th>
            <th>{t("taskList.startTime", "Start Time")}</th>
            <th>{t("taskList.endTime", "End Time")}</th>
            <th>{t("taskList.duration", "Duration")}</th>
            {renderActions && <th>{t("taskList.actions", "Actions")}</th>}
          </tr>
        </thead>
        <tbody>
          {tasks.length === 0 ? (
            <tr>
              <td colSpan={renderActions ? 6 : 5} className="bpm-table-empty">
                {loading
                  ? t("taskList.loading", "Loading...")
                  : t("taskList.noHistory", "No completed tasks")}
              </td>
            </tr>
          ) : (
            tasks.map((task) => (
              <tr key={task.id}>
                <td>{renderTaskName ? renderTaskName(task) : task.name}</td>
                <td className="bpm-mono">{task.processInstanceId?.slice(0, 8)}...</td>
                <td>{formatDate(task.startTime)}</td>
                <td>{formatDate(task.endTime)}</td>
                <td>{formatDuration(task.startTime, task.endTime)}</td>
                {renderActions && <td>{renderActions(task)}</td>}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export type { HistoryTask };
